import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';

function LandlordViewings() {
  const { user } = useContext(AuthContext);
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (!user?.token) return;
    const fetchViewings = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/viewings`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch viewings');
        const data = await res.json();
        setViewings(data.viewings || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchViewings();
  }, [user]);

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Cancel this viewing?')) return;
    setCancellingId(bookingId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/viewings/${bookingId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to cancel');
      }
      setViewings(prev => prev.map(v => v.id === bookingId ? { ...v, status: 'cancelled' } : v));
    } catch (err) {
      alert(err.message);
    } finally {
      setCancellingId(null);
    }
  };

  const now = new Date();
  const upcoming = viewings.filter(v => v.status === 'confirmed' && new Date(v.start_time) >= now);
  const past = viewings.filter(v => v.status !== 'confirmed' || new Date(v.start_time) < now);

  if (loading) return <main className="App-main"><p>Loading viewings...</p></main>;
  if (error) return <main className="App-main"><p className="error-message">{error}</p></main>;

  return (
    <main className="App-main">
      <h2>Viewings</h2>

      {upcoming.length === 0 && past.length === 0 && (
        <div className="dashboard-section" style={{ textAlign: 'center', padding: '2rem' }}>
          <p className="status-text">No viewings yet. Add viewing time slots to your properties so tenants can book.</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem' }}>Upcoming</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {upcoming.map(v => {
              const start = new Date(v.start_time);
              const dateStr = start.toLocaleDateString('en-CA', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric' });
              const timeStr = start.toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: 'numeric', minute: '2-digit' });
              const tenantName = [v.tenant_first_name, v.tenant_last_name].filter(Boolean).join(' ') || v.tenant_email || 'Tenant';
              return (
                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color)', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{dateStr}, {timeStr}</strong>
                    <p className="status-text" style={{ margin: '0.15rem 0 0', fontSize: '0.85rem' }}>{v.property_address}</p>
                    <p className="status-text" style={{ margin: '0.1rem 0 0', fontSize: '0.85rem' }}>Tenant: {tenantName}</p>
                  </div>
                  <button
                    className="secondary-button"
                    style={{ padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}
                    onClick={() => handleCancel(v.id)}
                    disabled={cancellingId === v.id}
                  >
                    {cancellingId === v.id ? 'Cancelling...' : 'Cancel'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <div className="dashboard-section">
          <h3 style={{ margin: '0 0 0.75rem' }}>Past / Cancelled</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {past.map(v => {
              const start = new Date(v.start_time);
              const dateStr = start.toLocaleDateString('en-CA', { timeZone: 'America/Toronto', weekday: 'short', month: 'short', day: 'numeric' });
              const timeStr = start.toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: 'numeric', minute: '2-digit' });
              const tenantName = [v.tenant_first_name, v.tenant_last_name].filter(Boolean).join(' ') || v.tenant_email || 'Tenant';
              return (
                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color)', opacity: 0.6, flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <strong style={{ fontSize: '0.95rem' }}>{dateStr}, {timeStr}</strong>
                    <span className="report-chip" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>{v.status === 'cancelled' ? 'Cancelled' : 'Past'}</span>
                    <p className="status-text" style={{ margin: '0.15rem 0 0', fontSize: '0.85rem' }}>{v.property_address}</p>
                    <p className="status-text" style={{ margin: '0.1rem 0 0', fontSize: '0.85rem' }}>Tenant: {tenantName}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </main>
  );
}

export default LandlordViewings;
